/**
 * One viewport's positional shadow atlas: the slots Godot's atlas gives its lights, laid out in the
 * shared atlas texture (`shadowAtlasTarget.ts`), and the cube each omni light renders for that
 * viewport. A light two viewports render holds a cube for each, as Godot holds a slot in each atlas.
 * One cube per light would reallocate whenever the two slots differ.
 */
import { PositionalShadowAtlas, } from '../../godot/rendering/lights/positionalShadowAtlas.js';
import { holdPositionalShadowAtlas, releasePositionalShadowAtlas } from './shadowAtlasTarget.js';
/** Written only by `ViewportShadowAtlas.bind`. Cleared for a shadow when its atlas is disposed. */
// eslint-disable-next-line no-restricted-syntax -- ViewportShadowAtlas.bind writes an entry from outside, and dispose deletes it
const boundAtlas = new WeakMap();
export class ViewportShadowAtlas {
    slots;
    /** The cubes this viewport's lights rendered last, kept while another viewport renders them. */
    parkedMaps = new Map();
    /** The shadows whose cube is this viewport's now. */
    boundShadows = new Set();
    constructor(settings) {
        this.slots = new PositionalShadowAtlas(settings);
        holdPositionalShadowAtlas(this, this.slots.size);
    }
    /** One render's allocation, in Godot's order (`PositionalShadowAtlas.allocate`). */
    allocate(requests, tickMsec) {
        this.slots.allocate(requests, tickMsec);
    }
    /** The slot the light holds, or null when it holds none. */
    slot(light) {
        return this.slots.slot(light);
    }
    /**
     * Gives an omni light's shadow the cube it rendered last for this viewport, and parks the cube it
     * holds for the viewport that rendered it before. A cube made before any viewport bound it stays.
     */
    bind(shadow) {
        const current = boundAtlas.get(shadow);
        if (current === this)
            return;
        if (current) {
            current.park(shadow);
            shadow.map = this.parkedMaps.get(shadow) ?? null;
            this.parkedMaps.delete(shadow);
        }
        boundAtlas.set(shadow, this);
        this.boundShadows.add(shadow);
    }
    /**
     * Frees the slot and the parked cube of every light that left the scene. A bound cube stays with its
     * light, which disposes it when it unmounts.
     */
    retain(lights) {
        for (const owner of this.slots.ownersHoldingSlots()) {
            if (!lights.has(owner))
                this.slots.release(owner);
        }
        for (const [shadow, map] of this.parkedMaps) {
            if (isShadowOfAny(lights, shadow))
                continue;
            map.dispose();
            this.parkedMaps.delete(shadow);
        }
        for (const shadow of this.boundShadows) {
            if (!isShadowOfAny(lights, shadow))
                this.unbind(shadow);
        }
    }
    /**
     * Disposes every parked cube and lets go of the atlas texture. A bound cube stays with its light, and
     * the next viewport adopts it.
     */
    dispose() {
        for (const map of this.parkedMaps.values())
            map.dispose();
        this.parkedMaps.clear();
        for (const shadow of this.boundShadows)
            this.unbind(shadow);
        releasePositionalShadowAtlas(this);
    }
    park(shadow) {
        if (shadow.map)
            this.parkedMaps.set(shadow, shadow.map);
        this.unbind(shadow);
    }
    unbind(shadow) {
        this.boundShadows.delete(shadow);
        if (boundAtlas.get(shadow) === this)
            boundAtlas.delete(shadow);
    }
}
/** A loop, not a set of the shadows: the fit retains on every render. */
function isShadowOfAny(lights, shadow) {
    for (const light of lights)
        if (light.shadow === shadow)
            return true;
    return false;
}
/** Pushed only by `renderWithShadowAtlas` around one render, and popped when that render returns. */
const activeAtlases = [];
/** Runs `render` with `atlas` as the atlas of every scene it renders: a SubViewport's pass. */
export function renderWithShadowAtlas(atlas, render) {
    activeAtlases.push(atlas);
    try {
        render();
    }
    finally {
        activeAtlases.pop();
    }
}
/** The atlas of the SubViewport rendering now, or null for a render of the main view. */
export function activeShadowAtlas() {
    return activeAtlases[activeAtlases.length - 1] ?? null;
}
//# sourceMappingURL=viewportShadowAtlas.js.map